<template>
  <div :ref="(el: any) => { viewport_inner = el }" tabindex="0" @keydown.down.exact.prevent="trackArrowDown(1)" @keydown.up.exact.prevent="trackArrowUp(1)"
  @keydown.enter.prevent="toggleCurrent" class="no-outline">
    <div v-if="returnType === 'none' || !elements">
      <recursive-selector-element
        v-for="(element, index) in theModel"
        :ref="(el: any) => { divs[index] = el }"
        :key="index"
        :element-chain="takeIdsOnly ? (elementType === 1 ? [element.Id] : (elementType === 9 ? [element[entityInsideName].Id] : [element])) : [element]"
        :parent-idx="index"
        :is-root="true"
        :return-type="returnType"
        :nested-key-name="nestedKeyName"
        :element-type="elementType"
        :take-ids-only="takeIdsOnly"
        :entity-inside-name="entityInsideName"
        :allow-multiple="allowMultiple"
        :hide-checkboxes="hideCheckboxes"
        :show-records="showRecords"
        :is-column="isColumn"
        :open-on-start="openOnStart"
        :filter-out-list="filterOutList"
        :parameter-type-name="parameterTypeName"
        :value-type="element.ValueType"
        :search-regex="searchRegex"
        :filter-string="filterString"
        :class="['q-py-none', { 'rec-lst-subselection': index_selected === index }]"
        @elementToggled="elementToggled(index)"
        v-model="theModel[index]"
      />
    </div>
    <div v-else>
      <recursive-selector-element
        v-for="(element, index) in elements"
        :ref="(el: any) => { divs[index] = el }"
        :key="index"
        :element="element"
        :element-chain="takeIdsOnly ? (elementType === 1 ? [element.Id] : (elementType === 9 ? [element[entityInsideName].Id] : [element])) : [element]"
        :parent-idx="index"
        :is-root="true"
        :return-type="returnType"
        :nested-key-name="nestedKeyName"
        :element-type="elementType"
        :take-ids-only="takeIdsOnly"
        :entity-inside-name="entityInsideName"
        :allow-multiple="allowMultiple"
        :hide-checkboxes="hideCheckboxes"
        :show-records="showRecords"
        :is-column="isColumn"
        :open-on-start="openOnStart"
        :filter-out-list="filterOutList"
        :parameter-type-name="parameterTypeName"
        :value-type="element.ValueType"
        :search-regex="searchRegex"
        :filter-string="filterString"
        :class="['q-py-none', { 'rec-lst-subselection': index_selected === index }]"
        @elementToggled="elementToggled(index)"
        v-model="theModel"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue, Watch } from 'vue-facing-decorator';
import { VariableType } from '@/models/etc/types';
import RecursiveSelectorElement from './RecursiveSelectorElement.vue';
import { reactive } from 'vue';

function isInViewport(element: any, viewport: any) {
  if (!element || !viewport) { return false }
  const rect = element.getBoundingClientRect();
  const viewport_rect = viewport.getBoundingClientRect();

  return (
    rect.top >= viewport_rect.top &&
    rect.left >= viewport_rect.left &&
    rect.bottom <= viewport_rect.bottom &&
    rect.right <= viewport_rect.right
  );
}

/**
 * List component displaying v-model with recursive options functionality
 * */
@Component({
  name: 'RecursiveSelector',
  components: { RecursiveSelectorElement },
  expose: ['trackArrowUp', 'trackArrowDown', 'toggleCurrent', 'selectCurrent' ]
})
export default class RecursiveSelector extends Vue {
  // viewport to check visibility
  @Prop({ default: undefined })
  viewport: any
  // v-model input
  @Prop({ default: undefined })
  elements?: any[]
  // v-model input

  // Здесь важно понимать, что в элементе может быть любой класс.
  // Конкретная интерпретация зависит от того, какой отображающий компонент 
  // из data-components стоит в RecursiveSelectorElement.

  // В модель вернётся массив выбранных элементов 
  // (или цепочек элементов вверх по рекурсии до родительских элементов)
  @Prop({ default: [] as any[] })
  modelValue!: any[]
  // update v-model
  @Emit("update:modelValue") emitModelValue(arg: any[]) { return arg; }

  // works if elementType === VariableType.Entity
  @Prop({ default: false })
  takeIdsOnly?: boolean
  @Prop({ default: false })
  allowMultiple?: boolean
  @Prop({ default: false })
  hideCheckboxes?: boolean
  @Prop({ default: true })
  showRecords?: boolean
  @Prop({ default: false })
  isColumn!: string
  @Prop({ default: 'valueType' })
  parameterTypeName!: string
  // if false then the model is an array of selected elements (e.g. [selected_el1, selected_el2, ...])
  // if true then the model is an array of selected element chains 
  // (e.g. [[selected_el1_root, ..., selected_el1_parent, selected_el1], [selected_el2_root, ..., selected_el2_parent, selected_el2], ...])
  @Prop({ default: 'single-element' })
  returnType?: string
  @Prop({ default: VariableType.Entity })
  elementType!: VariableType
  @Prop({ default: '' })
  nestedKeyName!: string
  @Prop({ default: '' })
  entityInsideName!: string
  @Prop({ default: '' })
  filterString?: string
  @Prop({ default: false })
  openOnStart!: boolean

  @Prop({ default: 0 })
  valueType!: number
  @Prop({ default: [] })
  filterOutList?: any[]

  // selection expire time
  timeout: number | undefined = undefined
  // selection index for arrow (up and down) keys
  index_selected = -1
  // dynamic item refs
  divs: any[] = reactive([])
  viewport_inner: any = undefined

  get theModel(): any[] {
    return this.modelValue
  }

  set theModel(val: any[]) {
    this.emitModelValue(val)
  }

  get searchRegex() {
    if (!this.filterString) return null;

    return new RegExp('('+
    this.filterString.toLowerCase().replace(/[-[\]{}()*+?.,\\^$|#]/g, '\\$&').split(' ')
    .filter((s: string) => s !== '')
    .map((s: string) => {
      return '.*('+s+')'
    }).join('')+'.*)', 'gmi');
  }

  // selection hooks
  created() {
    this.index_selected = -1
  }

  trackArrowUp(step: number, reset_index: boolean = false) {
    if (reset_index) {
      this.index_selected = -1;
    }

    let max_index = this.returnType === 'none' || !this.elements ? this.theModel.length : this.elements.length

    // find first visible element from the bottom
    if (this.index_selected < 0) {
      this.index_selected = max_index-1
      for (let i = max_index-1; i >= 0; i--) {
        // @ts-ignore
        if (this.divs[i].$el.tagName && isInViewport(this.divs[i].$el, this.viewport || this.viewport_inner)) {
          this.index_selected = i
          break;
        }
      }
    } else {
      // decrement if timer has not expired
      (this.index_selected > 0) && (this.index_selected -= step)

      // find first visible
      while ((!this.divs[this.index_selected] || !this.divs[this.index_selected].$el.tagName) && 
      this.index_selected > 0) {
        this.index_selected--
      }

      // if not visible the scroll to element
      if (this.index_selected >= 0) {
        if (this.divs[this.index_selected] && this.divs[this.index_selected].$el && 
        this.divs[this.index_selected].$el.tagName && 
          // @ts-ignore
          !isInViewport(this.divs[this.index_selected].$el, this.viewport || this.viewport_inner)) {
            this.divs[this.index_selected].$el.scrollIntoView({ behavior: "smooth", block: 'center' })
        }
      } else {
        let ind = 0;
        // find first visible
        while (!this.divs[ind].$el.tagName && ind < this.divs.length-1) {
          ind++
        }
        
        this.divs[ind] && this.divs[ind].$el && 
        this.divs[ind].$el.tagName && (this.divs[ind].$el.scrollIntoView({ behavior: "smooth", block: 'center' }))
      }
    }

    const that = this
    // clear previous timeout
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = undefined
    }
    this.timeout = setTimeout(() => {
      that.index_selected = -1
      that.timeout = undefined
    }, 2000)
  }

  trackArrowDown(step: number, reset_index: boolean = false) {
    if (reset_index) {
      this.index_selected = -1;
    }

    let max_index = this.returnType === 'none' || !this.elements ? this.theModel.length : this.elements.length

    // find first visible element from the top
    if (this.index_selected < 0) {
      this.index_selected = 0
      for (let i = 0; i < max_index; i++) {
        // @ts-ignore
        if (this.divs[i].$el.tagName && isInViewport(this.divs[i].$el, this.viewport || this.viewport_inner)) {
          this.index_selected = i
          break;
        }
      }
    } else {
      // increment if timer has not expired
      (this.index_selected < this.divs.length) && (this.index_selected += step)

      // find first visible
      while ((!this.divs[this.index_selected] || !this.divs[this.index_selected].$el.tagName) && 
      this.index_selected < this.divs.length-1) {
        this.index_selected++
      }

      // if not visible the scroll to element
      if (this.index_selected < this.divs.length) {
        if (this.divs[this.index_selected] && this.divs[this.index_selected].$el && this.divs[this.index_selected].$el.tagName && 
          // @ts-ignore
          !isInViewport(this.divs[this.index_selected].$el, this.viewport || this.viewport_inner)) {
            this.divs[this.index_selected].$el.scrollIntoView({ behavior: "smooth", block: 'center' })
        }
      } else {
        let ind = this.divs.length-1;
        // find first visible
        while (!this.divs[ind].$el && ind > 0) {
          ind--
        }

        this.divs[ind] && this.divs[ind].$el && 
        this.divs[ind].$el.tagName && this.divs[ind].$el.scrollIntoView({ behavior: "smooth", block: 'center' })
      }
    }

    const that = this
    // clear previous timeout
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = undefined
    }
    // keep selection for 2s only
    this.timeout = setTimeout(() => {
      that.index_selected = -1
      that.timeout = undefined
    }, 2000)
  }

  toggleCurrent() {
    this.toggleElement(this.index_selected)
  }

  selectCurrent() {
    this.toggleCurrent()
  }

  // selection methods
  // check/uncheck the element
  toggleElement(input_i: number) {
    let max_index = this.returnType === 'none' || !this.elements ? this.theModel.length : this.elements.length
    if (input_i < 0 || input_i >= max_index || !this.divs[input_i]) { return }

    this.divs[input_i] && (this.divs[input_i] as any).toggle()
  }

  // unselection methods
  elementToggled(input_i: number) {
    let max_index = this.returnType === 'none' || !this.elements ? this.theModel.length : this.elements.length
    if (input_i < 0 || input_i >= max_index) { return }

    const that = this// set starting index for keyboard
    this.index_selected = input_i
    // clear previous timeout
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = undefined
    }
    // keep selection for 2s only
    this.timeout = setTimeout(() => {
      that.index_selected = -1
      that.timeout = undefined
    }, 2000)
  }
}
</script>

<style lang="scss">
.rec-lst-item {
  cursor: pointer;
  width: 100%;
  transition: all 0.05s ease-in-out;
  border-radius: 3px;
  &:hover {
    background-color: $secondary;
  }
}

.rec-lst-subselection {
  background-color: $secondary !important;
  transition: all 0.1s ease-out;
  cursor: pointer;
}

.no-outline:focus-visible {
  outline: 0;
}
</style>